import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Wind,
  Wrench,
  RefreshCw,
  Zap,
  Building2,
  Home,
  ChevronDown,
  Phone,
  MapPin,
} from 'lucide-react';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';

const META_DESCRIPTION =
  'Instalação, manutenção e substituição de ar-condicionado em Salir e Algarve. Electro Salir – especialistas locais em climatização. Orçamentos grátis. 289 489 848 | 917 869 307.';

export default function SiteArCondicionado() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevMeta = document.querySelector('meta[name="description"]')?.getAttribute('content');
    document.title = 'Instalação de Ar-Condicionado em Salir e Algarve | Electro Salir';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', META_DESCRIPTION);
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevMeta) metaDesc.setAttribute('content', prevMeta);
    };
  }, []);

  // JSON-LD para SEO e motores de busca/IA
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Qual o melhor ar-condicionado para casa?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Depende do tamanho da divisão e de quantas divisões quer climatizar. Para uma só divisão, um split inverter com boa eficiência energética costuma ser a opção mais equilibrada. Para várias divisões, o multi-split é prático. Consulte-nos e analisamos o seu caso.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quanto consome?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O consumo varia conforme a potência, a eficiência e o tempo de utilização. Equipamentos inverter de classe A ou superior consomem significativamente menos que modelos antigos. Um técnico pode ajudá-lo a estimar o consumo consoante o seu uso típico.',
        },
      },
      {
        '@type': 'Question',
        name: 'Precisa de manutenção?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim. Limpar ou substituir filtros periodicamente e verificar o estado geral do equipamento prolonga a vida útil e mantém a eficiência. Recomendamos pelo menos uma inspecção por ano, sobretudo antes da época de maior uso.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quanto tempo demora a instalação?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Uma instalação simples de um split pode levar algumas horas. Um multi-split ou sistemas mais complexos podem exigir um ou mais dias, conforme o projecto. Indicamos sempre um prazo estimado no orçamento.',
        },
      },
    ],
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Electro Salir',
    description: 'Instalação, manutenção e substituição de ar-condicionado em Salir e Algarve.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rua Manuel Dourado Eusébio N.º 34 A',
      addressLocality: 'Salir',
      postalCode: '8100-202',
      addressRegion: 'Algarve',
      addressCountry: 'PT',
    },
    telephone: ['+351289489848', '+351917869307'],
    email: 'electrosalir@sapo.pt',
    areaServed: { '@type': 'Place', name: 'Salir e Algarve' },
  };

  const heroBg = `${process.env.PUBLIC_URL || ''}/hero-background.png`;

  return (
    <div className="min-h-screen bg-gestao-background text-slate-800">
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      <SiteHeader />
      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gestao-primary font-semibold hover:underline mb-8"
          >
            <ArrowLeft size={18} />
            Voltar ao início
          </Link>

          {/* Hero com imagem para SEO */}
          <header
            className="relative rounded-2xl overflow-hidden mb-12 bg-slate-800"
            style={{ minHeight: '200px' }}
          >
            <img
              src={heroBg}
              alt="Instalação de ar-condicionado em Salir e Algarve – Electro Salir, climatização e conforto térmico"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
            <div className="relative z-10 p-8 sm:p-12 flex flex-col justify-center">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 drop-shadow-md">
                Instalação de Ar-Condicionado em Salir e Algarve | Electro Salir
              </h1>
              <p className="text-lg text-slate-200 max-w-2xl">
                Conforto térmico ao longo do ano, com a experiência e proximidade de quem conhece Salir e o Algarve.
                Somos a Electro Salir – a sua empresa local de referência em climatização.
              </p>
            </div>
          </header>

          {/* Introdução */}
          <section className="mb-16">
            <p className="text-lg text-slate-600 leading-relaxed">
              O ar-condicionado é hoje um elemento essencial para o conforto em casa ou no trabalho.
              No Algarve, onde os verões são longos e quentes, ter um sistema de climatização fiável
              faz toda a diferença. Na Electro Salir, combinamos técnicos experientes com um
              atendimento personalizado. Conhecemos a região, as necessidades das famílias e das
              empresas locais, e estamos aqui para ajudar com soluções à medida.
            </p>
          </section>

          {/* H2 – Serviços de ar-condicionado */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Serviços de ar-condicionado
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Wind size={24} className="text-gestao-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Instalação</h3>
                <p className="text-slate-600 text-sm">
                  Instalamos equipamentos novos em casas e empresas. Planeamos a solução mais
                  adequada ao espaço e às suas necessidades, garantindo uma montagem segura e
                  correcta.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Wrench size={24} className="text-gestao-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Manutenção</h3>
                <p className="text-slate-600 text-sm">
                  A manutenção regular prolonga a vida do equipamento e mantém a eficiência.
                  Oferecemos planos de manutenção para particulares e empresas em Salir e região.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <RefreshCw size={24} className="text-gestao-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Substituição</h3>
                <p className="text-slate-600 text-sm">
                  Quando o equipamento já não compensa reparar, ajudamos na escolha e instalação de
                  um novo sistema, mais moderno e eficiente.
                </p>
              </div>
            </div>
          </section>

          {/* H2 – Tipos de ar-condicionado */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Tipos de ar-condicionado
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Split</h3>
                <p className="text-slate-600">
                  O mais comum em habitações. Um aparelho interior (evaporador) e um exterior
                  (condensador) ligados entre si. Ideal para uma divisão, como sala ou quarto.
                  Simples de instalar e económica.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Multi-split</h3>
                <p className="text-slate-600">
                  Um único condensador exterior ligado a vários evaporadores em várias divisões.
                  Solução prática para climatizar toda a casa ou escritório sem ocupar muito espaço
                  exterior.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Inverter</h3>
                <p className="text-slate-600">
                  A tecnologia inverter ajusta a potência do compressor conforme a necessidade,
                  em vez de ligar e desligar constantemente. Resultado: menor consumo eléctrico,
                  maior conforto e menos ruído. Recomendamos inverter sempre que possível.
                </p>
              </div>
            </div>
          </section>

          {/* H2 – Consumo e eficiência energética */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Consumo e eficiência energética
            </h2>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex gap-4 mb-4">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Zap size={24} className="text-gestao-primary" />
                </div>
                <div>
                  <p className="text-slate-600 leading-relaxed">
                    O consumo de um ar-condicionado depende da potência, da eficiência energética
                    (etiqueta A a G) e do uso que lhe der. Equipamentos inverter com classe A ou
                    superior consomem menos e compensam a médio prazo. A escolha correcta da potência
                    também é importante: um aparelho demasiado grande ou pequeno para o espaço pode
                    gastar mais. Na Electro Salir ajudamos a dimensionar a solução adequada e
                    escolhemos equipamentos de marcas reconhecidas, com boa relação
                    eficiência-preço.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* H2 – Casas e empresas */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Casas e empresas
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200 flex gap-4">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Home size={24} className="text-gestao-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Particulares</h3>
                  <p className="text-slate-600 text-sm">
                    Para a sua casa em Salir ou no Algarve, propomos soluções adaptadas ao espaço e
                    ao orçamento: split para uma divisão ou multi-split para climatizar toda a
                    habitação com conforto e eficiência.
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200 flex gap-4">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Building2 size={24} className="text-gestao-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Empresas</h3>
                  <p className="text-slate-600 text-sm">
                    Comércio, escritórios, restaurantes ou armazéns: cada espaço tem exigências
                    próprias. Oferecemos instalação, manutenção e assistência técnica para empresas
                    na região, com horários e planos à medida.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* H2 – Perguntas Frequentes */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Perguntas Frequentes
            </h2>
            <div className="space-y-4">
              <details className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  Qual o melhor ar-condicionado para casa?
                  <ChevronDown size={20} className="text-slate-400 group-open:rotate-180 transition-transform shrink-0 ml-2" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                  Depende do tamanho da divisão e de quantas divisões quer climatizar. Para uma só
                  divisão, um split inverter com boa eficiência energética costuma ser a opção mais
                  equilibrada. Para várias divisões, o multi-split é prático. Consulte-nos e
                  analisamos o seu caso.
                </div>
              </details>
              <details className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  Quanto consome?
                  <ChevronDown size={20} className="text-slate-400 group-open:rotate-180 transition-transform shrink-0 ml-2" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                  O consumo varia conforme a potência, a eficiência e o tempo de utilização.
                  Equipamentos inverter de classe A ou superior consomem significativamente menos
                  que modelos antigos. Um técnico pode ajudá-lo a estimar o consumo consoante o seu
                  uso típico.
                </div>
              </details>
              <details className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  Precisa de manutenção?
                  <ChevronDown size={20} className="text-slate-400 group-open:rotate-180 transition-transform shrink-0 ml-2" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                  Sim. Limpar ou substituir filtros periodicamente e verificar o estado geral do
                  equipamento prolonga a vida útil e mantém a eficiência. Recomendamos pelo menos
                  uma inspecção por ano, sobretudo antes da época de maior uso.
                </div>
              </details>
              <details className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  Quanto tempo demora a instalação?
                  <ChevronDown size={20} className="text-slate-400 group-open:rotate-180 transition-transform shrink-0 ml-2" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                  Uma instalação simples de um split pode levar algumas horas. Um multi-split ou
                  sistemas mais complexos podem exigir um ou mais dias, conforme o projecto.
                  Indicamos sempre um prazo estimado no orçamento.
                </div>
              </details>
            </div>
          </section>

          {/* H2 – Contacto */}
          <section className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Contacto</h2>
            <div className="bg-gestao-primary text-white rounded-2xl p-8 sm:p-10">
              <p className="text-lg mb-6 text-white/95">
                Precisa de orçamento para instalação, manutenção ou substituição de ar-condicionado
                em Salir ou no Algarve? Contacte a Electro Salir. Estamos perto de si e prontos
                para ajudar.
              </p>
              <div className="flex flex-wrap gap-4 sm:gap-6">
                <a
                  href="tel:+351289489848"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gestao-primary font-semibold rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <Phone size={20} />
                  289 489 848
                </a>
                <a
                  href="tel:+351917869307"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gestao-primary font-semibold rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <Phone size={20} />
                  917 869 307
                </a>
              </div>
              <div className="mt-6 flex items-start gap-3 text-white/90">
                <MapPin size={20} className="shrink-0 mt-0.5" />
                <span>
                  Rua Manuel Dourado Eusébio N.º 34 A, Salir, 8100-202 Portugal
                </span>
              </div>
              <Link
                to="/contactos"
                className="inline-block mt-6 text-white font-semibold underline hover:no-underline"
              >
                Ver página de contactos →
              </Link>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
